import { useState, useMemo, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Input } from '../../ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../ui/alert-dialog';
import { Label } from '../../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Textarea } from '../../ui/textarea';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../ui/dropdown-menu';
import { Progress } from '../../ui/progress';
import {
  Warehouse,
  Plus,
  Search,
  Filter,
  MoreVertical,
  Edit,
  Eye,
  AlertTriangle,
  Ban,
  Package,
  TrendingUp,
  TrendingDown,
  Save,
  ShoppingCart,
  FileText,
  Building,
  Loader2,
  Settings,
  MapPin,
  PowerOff,
  ChevronDown
} from 'lucide-react';
import { useAuth } from '../../../src/hooks/useAuth';
import { useInventoryItems, useWarehouses, useWarehouseEnums } from '../../../src/hooks/queries/use-inventory-items';
import { procurementService, InventoryItemBackend, WarehouseBackend, WarehouseType } from '../../../src/services/procurement.service';
import { useProjects } from '../../../src/hooks/queries/use-projects';
import { queryKeys } from '../../../src/lib/query-keys';
import { showSuccess, showError } from '../../../src/utils/notification.utils';
import { useOrgCurrency } from '../../../src/hooks/useOrgCurrency';
import { formatCurrency, formatCurrencyCompact } from '../../../src/utils/currency';

const CATEGORIES = [
  'Concrete & Cement', 'Steel & Metals', 'Aggregates & Sand', 'Timber & Wood Products',
  'Electrical Supplies', 'Plumbing & HVAC', 'Insulation Materials', 'Roofing Materials',
  'Paint & Coatings', 'Hardware & Fasteners', 'Safety Equipment', 'Tools & Machinery',
  'Glass & Glazing', 'Flooring Materials', 'Masonry & Bricks', 'Waterproofing',
  'Landscaping Materials', 'Prefabricated Components', 'Architectural Elements', 'Specialty Chemicals'
];

const UNITS = ['pcs', 'kg', 'm', 'm²', 'm³', 'tons', 'bags', 'rolls', 'sheets', 'boxes', 'liters', 'gallons'];

// ─── Add / Edit Material Modal ───────────────────────────────────────────────

interface MaterialModalProps {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  mode: 'add' | 'edit';
  item?: InventoryItemBackend;
  orgId: string;
  orgCurrency: string;
  warehouses: { warehouse_id: string; warehouse_name: string }[];
  onSuccess: () => void;
}

function MaterialModal({ open, onOpenChange, mode, item, orgId, orgCurrency, warehouses, onSuccess }: MaterialModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    materialName: item?.material_name ?? '',
    category: item?.category ?? '',
    description: item?.description ?? '',
    warehouseId: item?.warehouse_id ?? '',
    unitCost: item?.unit_cost != null ? String(item.unit_cost) : '',
    quantity: item?.current_stock != null ? String(item.current_stock) : '0',
    unit: item?.unit_of_measure ?? '',
    minimumLevel: item?.min_stock_level != null ? String(item.min_stock_level) : '',
    maximumLevel: item?.max_stock_level != null ? String(item.max_stock_level) : '',
    leadTime: item?.lead_time_days != null ? String(item.lead_time_days) : '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validate = () => {
    const e: Record<string, string> = {};
    if (!formData.materialName.trim()) e.materialName = 'Material name is required';
    if (!formData.unit) e.unit = 'Unit is required';
    if (formData.unitCost && isNaN(Number(formData.unitCost))) e.unitCost = 'Must be a number';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setIsSubmitting(true);
    try {
      const payload = {
        material_name: formData.materialName.trim(),
        unit_of_measure: formData.unit,
        current_stock: formData.quantity ? Number(formData.quantity) : 0,
        unit_cost: formData.unitCost ? Number(formData.unitCost) : undefined,
        min_stock_level: formData.minimumLevel ? Number(formData.minimumLevel) : undefined,
        max_stock_level: formData.maximumLevel ? Number(formData.maximumLevel) : undefined,
        category: formData.category || undefined,
        description: formData.description || undefined,
        warehouse_id: formData.warehouseId || undefined,
        lead_time_days: formData.leadTime ? Number(formData.leadTime) : undefined,
      };

      if (mode === 'add') {
        await procurementService.createInventoryItemBackend({ organization_id: orgId, ...payload });
        showSuccess({ title: 'Material Added', message: `${formData.materialName} has been added to inventory` });
      } else {
        await procurementService.updateInventoryItemBackend(item!.inventory_item_id, payload);
        showSuccess({ title: 'Material Updated', message: `${formData.materialName} has been updated` });
      }
      onSuccess();
      onOpenChange(false);
    } catch (err: any) {
      showError({ title: mode === 'add' ? 'Failed to Add Material' : 'Failed to Update Material', message: err?.message ?? 'An error occurred' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const totalValue = (Number(formData.unitCost) || 0) * (Number(formData.quantity) || 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto modal-content">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            {mode === 'add' ? 'Add New Material' : 'Edit Material'}
          </DialogTitle>
          <DialogDescription>
            {mode === 'add' ? 'Add new material to inventory' : 'Update material information'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Package className="h-4 w-4 text-primary" />
              <h4 className="font-medium">Material Information</h4>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Material Name *</Label>
                <Input
                  value={formData.materialName}
                  onChange={(e) => setFormData({ ...formData, materialName: e.target.value })}
                  placeholder="e.g., Reinforcement Steel Bars"
                />
                {errors.materialName && <p className="text-sm text-destructive">{errors.materialName}</p>}
              </div>
              <div className="space-y-2">
                <Label>Category</Label>
                <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                  <SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Detailed description, specifications, grade..."
                rows={2}
              />
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Warehouse className="h-4 w-4 text-primary" />
              <h4 className="font-medium">Storage & Cost</h4>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Warehouse</Label>
                <Select value={formData.warehouseId} onValueChange={(v) => setFormData({ ...formData, warehouseId: v })}>
                  <SelectTrigger><SelectValue placeholder="Select warehouse" /></SelectTrigger>
                  <SelectContent>
                    {warehouses.filter(w => w.warehouse_id).map(w => <SelectItem key={w.warehouse_id} value={w.warehouse_id}>{w.warehouse_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Unit Cost</Label>
                <Input
                  type="number"
                  value={formData.unitCost}
                  onChange={(e) => setFormData({ ...formData, unitCost: e.target.value })}
                  placeholder="0.00"
                  step="0.01"
                />
                {errors.unitCost && <p className="text-sm text-destructive">{errors.unitCost}</p>}
              </div>
              <div className="space-y-2">
                <Label>Total Value</Label>
                <Input value={formatCurrency(totalValue, orgCurrency)} readOnly className="bg-muted" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Quantity *</Label>
                <Input
                  type="number"
                  value={formData.quantity}
                  onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                  placeholder="0"
                  step="0.001"
                />
              </div>
              <div className="space-y-2">
                <Label>Unit *</Label>
                <Select value={formData.unit} onValueChange={(v) => setFormData({ ...formData, unit: v })}>
                  <SelectTrigger><SelectValue placeholder="Select unit" /></SelectTrigger>
                  <SelectContent>
                    {UNITS.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                  </SelectContent>
                </Select>
                {errors.unit && <p className="text-sm text-destructive">{errors.unit}</p>}
              </div>
              <div className="space-y-2">
                <Label>Lead Time (Days)</Label>
                <Input
                  type="number"
                  value={formData.leadTime}
                  onChange={(e) => setFormData({ ...formData, leadTime: e.target.value })}
                  placeholder="7"
                />
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-primary" />
              <h4 className="font-medium">Stock Level Thresholds</h4>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Minimum Level</Label>
                <Input
                  type="number"
                  value={formData.minimumLevel}
                  onChange={(e) => setFormData({ ...formData, minimumLevel: e.target.value })}
                  placeholder="Reorder point"
                  step="0.001"
                />
              </div>
              <div className="space-y-2">
                <Label>Maximum Level</Label>
                <Input
                  type="number"
                  value={formData.maximumLevel}
                  onChange={(e) => setFormData({ ...formData, maximumLevel: e.target.value })}
                  placeholder="Maximum stock"
                  step="0.001"
                />
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" disabled={isSubmitting} onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSave} disabled={isSubmitting}>
            <Save className="h-4 w-4 mr-2" />
            {isSubmitting ? 'Saving...' : mode === 'add' ? 'Add Material' : 'Update Material'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Adjust Stock Modal ───────────────────────────────────────────────────────

interface AdjustStockModalProps {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  item: InventoryItemBackend;
  onSuccess: () => void;
}

function AdjustStockModal({ open, onOpenChange, item, onSuccess }: AdjustStockModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [adjustment, setAdjustment] = useState('');
  const [reason, setReason] = useState('');

  const handleSave = async () => {
    const qty = Number(adjustment);
    if (isNaN(qty) || qty === 0) {
      showError({ title: 'Invalid Adjustment', message: 'Enter a non-zero adjustment quantity' });
      return;
    }
    if (!reason.trim()) {
      showError({ title: 'Reason Required', message: 'Please provide a reason for the adjustment' });
      return;
    }
    setIsSubmitting(true);
    try {
      await procurementService.adjustInventoryStock(item.inventory_item_id, qty, reason.trim());
      showSuccess({ title: 'Stock Adjusted', message: `Stock for ${item.material_name} has been adjusted by ${qty > 0 ? '+' : ''}${qty}` });
      onSuccess();
      onOpenChange(false);
    } catch (err: any) {
      showError({ title: 'Adjustment Failed', message: err?.message ?? 'An error occurred' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[450px] modal-content">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            Adjust Stock — {item.material_name}
          </DialogTitle>
          <DialogDescription>Current stock: {Number(item.current_stock)} {item.unit_of_measure}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Adjustment Quantity *</Label>
            <Input
              type="number"
              value={adjustment}
              onChange={(e) => setAdjustment(e.target.value)}
              placeholder="Use negative to decrease (e.g. -10)"
              step="0.001"
            />
          </div>
          <div className="space-y-2">
            <Label>Reason *</Label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Stocktake correction, wastage, damage..."
              rows={3}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" disabled={isSubmitting} onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSave} disabled={isSubmitting}>
            <Save className="h-4 w-4 mr-2" />
            {isSubmitting ? 'Saving...' : 'Apply Adjustment'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── View Details Modal ───────────────────────────────────────────────────────

interface ViewDetailsModalProps {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  item: InventoryItemBackend;
  warehouseName?: string;
  orgCurrency: string;
}

function ViewDetailsModal({ open, onOpenChange, item, warehouseName, orgCurrency }: ViewDetailsModalProps) {
  const stock = Number(item.current_stock);
  const min = Number(item.min_stock_level ?? 0);
  const max = Number(item.max_stock_level ?? 0);
  const pct = max > 0 ? Math.min(100, Math.round((stock / max) * 100)) : stock > 0 ? 100 : 0;
  const totalValueNum = item.unit_cost != null ? stock * Number(item.unit_cost) : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto modal-content">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Eye className="h-5 w-5" />
            Material Details
          </DialogTitle>
          <DialogDescription>{item.material_name}</DialogDescription>
        </DialogHeader>
        <div className="space-y-6 py-4 text-sm">
          <div className="space-y-3">
            <h4 className="font-medium flex items-center gap-2"><Package className="h-4 w-4 text-primary" />Basic Information</h4>
            <div className="grid grid-cols-2 gap-3">
              <div><span className="font-medium">Name:</span> {item.material_name}</div>
              <div><span className="font-medium">Category:</span> {item.category ?? '—'}</div>
              <div className="col-span-2"><span className="font-medium">Description:</span> {item.description ?? '—'}</div>
            </div>
          </div>
          <div className="space-y-3">
            <h4 className="font-medium flex items-center gap-2"><Warehouse className="h-4 w-4 text-primary" />Stock Information</h4>
            <div className="grid grid-cols-2 gap-3">
              <div><span className="font-medium">Current Stock:</span> {stock} {item.unit_of_measure}</div>
              <div><span className="font-medium">Unit Cost:</span> {item.unit_cost != null ? formatCurrency(Number(item.unit_cost), orgCurrency) : '—'}</div>
              <div><span className="font-medium">Total Value:</span> {totalValueNum != null ? formatCurrency(totalValueNum, orgCurrency) : 'N/A'}</div>
              <div><span className="font-medium">Warehouse:</span> {warehouseName ?? '—'}</div>
            </div>
            {max > 0 && (
              <div className="space-y-2">
                <div className="flex justify-between"><span>Stock Level</span><span>{pct}%</span></div>
                <Progress value={pct} className="h-2" />
              </div>
            )}
          </div>
          <div className="space-y-3">
            <h4 className="font-medium flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-primary" />Thresholds</h4>
            <div className="grid grid-cols-2 gap-3">
              <div><span className="font-medium">Min Level:</span> {item.min_stock_level != null ? Number(item.min_stock_level) : '—'}</div>
              <div><span className="font-medium">Max Level:</span> {item.max_stock_level != null ? Number(item.max_stock_level) : '—'}</div>
              <div><span className="font-medium">Lead Time:</span> {item.lead_time_days != null ? `${item.lead_time_days} days` : '—'}</div>
              <div><span className="font-medium">Status:</span> {item.is_active ? 'Active' : 'Inactive'}</div>
            </div>
          </div>
          <div className="space-y-3">
            <h4 className="font-medium flex items-center gap-2"><FileText className="h-4 w-4 text-primary" />Tracking</h4>
            <div className="grid grid-cols-2 gap-3">
              <div><span className="font-medium">Last Updated:</span> {new Date(item.last_updated_at).toLocaleDateString()}</div>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Deactivate Confirmation ──────────────────────────────────────────────────

interface DeactivateDialogProps {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  item: InventoryItemBackend;
  onSuccess: () => void;
}

function DeactivateDialog({ open, onOpenChange, item, onSuccess }: DeactivateDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleConfirm = async () => {
    setIsSubmitting(true);
    try {
      await procurementService.updateInventoryItemBackend(item.inventory_item_id, { is_active: false });
      showSuccess({ title: 'Item Deactivated', message: `${item.material_name} has been deactivated` });
      onSuccess();
      onOpenChange(false);
    } catch (err: any) {
      showError({ title: 'Deactivation Failed', message: err?.message ?? 'An error occurred' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="modal-content">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <Ban className="h-5 w-5" />
            Deactivate Material
          </AlertDialogTitle>
          <AlertDialogDescription>
            This will deactivate <strong>{item.material_name}</strong> and hide it from active inventory. The record is preserved. You can reactivate it via edit.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isSubmitting}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            onClick={handleConfirm}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Deactivating...' : 'Deactivate'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

// ─── Warehouse Management Dialog ─────────────────────────────────────────────

const WAREHOUSE_TYPE_LABELS: Record<WarehouseType, string> = {
  central: 'Central Warehouse',
  site_store: 'Site Store',
  transit: 'Transit',
};

const WAREHOUSE_TYPE_DESC: Record<WarehouseType, string> = {
  central: 'Company-wide facility, no project link required',
  site_store: 'On-site store — must be linked to a project',
  transit: 'Temporary holding for in-transit goods',
};

interface WarehouseFormProps {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  orgId: string;
  warehouse?: WarehouseBackend;
  projects: { project_id: string; project_name: string; start_date?: string; end_date?: string }[];
  onSaved: () => void;
}

function WarehouseFormDialog({ open, onOpenChange, orgId, warehouse, projects, onSaved }: WarehouseFormProps) {
  const isEdit = !!warehouse;
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    warehouse_name: warehouse?.warehouse_name ?? '',
    warehouse_type: (warehouse?.warehouse_type ?? 'central') as WarehouseType,
    location: warehouse?.location ?? '',
    project_id: warehouse?.project_id ?? '',
    start_date: warehouse?.start_date ?? '',
    end_date: warehouse?.end_date ?? '',
  });

  useEffect(() => {
    if (open) {
      setForm({
        warehouse_name: warehouse?.warehouse_name ?? '',
        warehouse_type: (warehouse?.warehouse_type ?? 'central') as WarehouseType,
        location: warehouse?.location ?? '',
        project_id: warehouse?.project_id ?? '',
        start_date: warehouse?.start_date ?? '',
        end_date: warehouse?.end_date ?? '',
      });
    }
  }, [open, warehouse]);

  // Auto-populate dates from selected project for site_store
  const handleProjectChange = (projectId: string) => {
    const proj = projects.find(p => p.project_id === projectId);
    setForm(prev => ({
      ...prev,
      project_id: projectId,
      start_date: proj?.start_date ?? prev.start_date,
      end_date: proj?.end_date ?? prev.end_date,
    }));
  };

  const handleTypeChange = (type: WarehouseType) => {
    setForm(prev => ({
      ...prev,
      warehouse_type: type,
      project_id: type !== 'site_store' ? '' : prev.project_id,
    }));
  };

  const handleSave = async () => {
    if (!form.warehouse_name.trim()) { showError({ title: 'Validation', message: 'Warehouse name is required' }); return; }
    if (form.warehouse_type === 'site_store' && !form.project_id) {
      showError({ title: 'Validation', message: 'Site Store must be linked to a project' }); return;
    }
    setSaving(true);
    try {
      if (isEdit) {
        await procurementService.updateWarehouse(warehouse!.warehouse_id, {
          warehouse_name: form.warehouse_name.trim(),
          warehouse_type: form.warehouse_type,
          location: form.location || undefined,
          project_id: form.warehouse_type === 'site_store' ? form.project_id : null,
          start_date: form.start_date || null,
          end_date: form.end_date || null,
        });
        showSuccess({ title: 'Warehouse Updated', message: form.warehouse_name });
      } else {
        await procurementService.createWarehouse({
          organization_id: orgId,
          warehouse_name: form.warehouse_name.trim(),
          warehouse_type: form.warehouse_type,
          location: form.location || undefined,
          project_id: form.warehouse_type === 'site_store' ? form.project_id : undefined,
          start_date: form.start_date || undefined,
          end_date: form.end_date || undefined,
        });
        showSuccess({ title: 'Warehouse Created', message: form.warehouse_name });
      }
      onSaved();
      onOpenChange(false);
    } catch (err: any) {
      showError({ title: 'Failed to Save', message: err?.message ?? 'An error occurred' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[560px] modal-content">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Warehouse className="h-5 w-5" />
            {isEdit ? 'Edit Warehouse' : 'New Warehouse'}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="grid gap-2">
            <Label>Warehouse Name *</Label>
            <Input value={form.warehouse_name} onChange={e => setForm(p => ({ ...p, warehouse_name: e.target.value }))} placeholder="e.g., Main Store, Site A Storage" />
          </div>
          <div className="grid gap-2">
            <Label>Type *</Label>
            <div className="grid grid-cols-3 gap-2">
              {(['central', 'site_store', 'transit'] as WarehouseType[]).map(t => (
                <button
                  key={t}
                  onClick={() => handleTypeChange(t)}
                  className={`rounded-lg border-2 p-3 text-left transition-all ${form.warehouse_type === t ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/40'}`}
                >
                  <p className="text-sm font-medium">{WAREHOUSE_TYPE_LABELS[t]}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{WAREHOUSE_TYPE_DESC[t]}</p>
                </button>
              ))}
            </div>
          </div>

          {form.warehouse_type === 'site_store' && (
            <div className="grid gap-2">
              <Label>Linked Project *</Label>
              <Select value={form.project_id} onValueChange={handleProjectChange}>
                <SelectTrigger><SelectValue placeholder="Select project" /></SelectTrigger>
                <SelectContent>
                  {projects.map(p => <SelectItem key={p.project_id} value={p.project_id}>{p.project_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="grid gap-2">
            <Label>Location / Address</Label>
            <Input value={form.location} onChange={e => setForm(p => ({ ...p, location: e.target.value }))} placeholder="Physical address or GPS coordinates" />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label>Start Date</Label>
              <Input type="date" value={form.start_date} onChange={e => setForm(p => ({ ...p, start_date: e.target.value }))} />
            </div>
            <div className="grid gap-2">
              <Label>End Date</Label>
              <Input type="date" value={form.end_date} onChange={e => setForm(p => ({ ...p, end_date: e.target.value }))} />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving} className="gap-2">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            {isEdit ? 'Save Changes' : 'Create Warehouse'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface WarehouseManagementDialogProps {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  orgId: string;
  onWarehousesChanged: () => void;
}

function WarehouseManagementDialog({ open, onOpenChange, orgId, onWarehousesChanged }: WarehouseManagementDialogProps) {
  const { data: warehouses = [], refetch } = useWarehouses(orgId ? orgId : null);
  const { data: rawProjects = [] } = useProjects(orgId ? orgId : null);
  const [formOpen, setFormOpen] = useState(false);
  const [editingWarehouse, setEditingWarehouse] = useState<WarehouseBackend | undefined>();
  const [deactivateTarget, setDeactivateTarget] = useState<WarehouseBackend | null>(null);
  const [deactivating, setDeactivating] = useState(false);

  const projects = (rawProjects as any[]).map(p => ({
    project_id: p.project_id,
    project_name: p.project_name,
    start_date: p.start_date,
    end_date: p.end_date,
  }));

  const projectMap = Object.fromEntries(projects.map(p => [p.project_id, p.project_name]));

  const handleSaved = () => {
    refetch();
    onWarehousesChanged();
  };

  const handleDeactivate = async () => {
    if (!deactivateTarget) return;
    setDeactivating(true);
    try {
      await procurementService.deactivateWarehouse(deactivateTarget.warehouse_id);
      showSuccess({ title: 'Warehouse Deactivated', message: deactivateTarget.warehouse_name });
      refetch();
      onWarehousesChanged();
    } catch (err: any) {
      showError({ title: 'Failed to Deactivate', message: err?.message });
    } finally {
      setDeactivating(false);
      setDeactivateTarget(null);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[720px] modal-content">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5" />
              Manage Warehouses
            </DialogTitle>
            <DialogDescription>Create and manage storage locations. Site Stores must be linked to a project.</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="flex justify-end">
              <Button size="sm" className="gap-2" onClick={() => { setEditingWarehouse(undefined); setFormOpen(true); }}>
                <Plus className="h-4 w-4" />New Warehouse
              </Button>
            </div>

            {warehouses.length === 0 ? (
              <div className="text-center py-10">
                <Warehouse className="h-8 w-8 text-muted-foreground/40 mx-auto mb-3" />
                <p className="text-muted-foreground text-sm">No warehouses yet. Create one to get started.</p>
              </div>
            ) : (
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Project</TableHead>
                      <TableHead>Location</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {warehouses.map((wh: WarehouseBackend) => (
                      <TableRow key={wh.warehouse_id} className={!wh.is_active ? 'opacity-50' : ''}>
                        <TableCell className="font-medium">{wh.warehouse_name}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-xs">
                            {WAREHOUSE_TYPE_LABELS[wh.warehouse_type] ?? wh.warehouse_type}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {wh.project_id ? projectMap[wh.project_id] ?? wh.project_id : '—'}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">{wh.location ?? '—'}</TableCell>
                        <TableCell>
                          {wh.is_active
                            ? <Badge className="bg-green-100 text-green-800 border-green-200 text-xs">Active</Badge>
                            : <Badge variant="secondary" className="text-xs">Inactive</Badge>
                          }
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Button variant="ghost" size="icon" disabled={!wh.is_active} onClick={() => { setEditingWarehouse(wh); setFormOpen(true); }}>
                              <Edit className="h-3.5 w-3.5" />
                            </Button>
                            {wh.is_active && (
                              <Button variant="ghost" size="icon" className="text-destructive" onClick={() => setDeactivateTarget(wh)}>
                                <PowerOff className="h-3.5 w-3.5" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <WarehouseFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        orgId={orgId}
        warehouse={editingWarehouse}
        projects={projects}
        onSaved={handleSaved}
      />

      <AlertDialog open={!!deactivateTarget} onOpenChange={v => { if (!v) setDeactivateTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Deactivate Warehouse?</AlertDialogTitle>
            <AlertDialogDescription>
              "{deactivateTarget?.warehouse_name}" will be deactivated. No new inventory movements will be allowed. This cannot be undone from the UI.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeactivate} disabled={deactivating} className="bg-destructive text-destructive-foreground">
              {deactivating ? 'Deactivating…' : 'Deactivate'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

// ─── Main Inventory Component ─────────────────────────────────────────────────

export function Inventory() {
  const { orgId } = useAuth();
  const orgCurrency = useOrgCurrency();
  const queryClient = useQueryClient();
  const { data: inventory = [], isLoading } = useInventoryItems(orgId);
  const { data: warehouses = [] } = useWarehouses(orgId);

  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterWarehouse, setFilterWarehouse] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  // Modal state — all rendered outside the table to avoid Radix hierarchy bugs
  const [addOpen, setAddOpen] = useState(false);
  const [warehouseManageOpen, setWarehouseManageOpen] = useState(false);
  const [editItem, setEditItem] = useState<InventoryItemBackend | null>(null);
  const [viewItem, setViewItem] = useState<InventoryItemBackend | null>(null);
  const [adjustItem, setAdjustItem] = useState<InventoryItemBackend | null>(null);
  const [deactivateItem, setDeactivateItem] = useState<InventoryItemBackend | null>(null);

  const refetch = () => queryClient.invalidateQueries({ queryKey: queryKeys.inventoryItems.all(orgId ?? '') });

  const warehouseMap = useMemo(() =>
    Object.fromEntries(warehouses.map(w => [w.warehouse_id, w.warehouse_name])),
    [warehouses]
  );

  const getItemStatus = (item: InventoryItemBackend) => {
    if (!item.is_active) return 'inactive';
    const stock = Number(item.current_stock);
    const min = item.min_stock_level != null ? Number(item.min_stock_level) : null;
    const max = item.max_stock_level != null ? Number(item.max_stock_level) : null;
    if (stock === 0) return 'out_of_stock';
    if (min != null && stock <= min * 0.5) return 'critical';
    if (min != null && stock <= min) return 'low';
    if (max != null && stock >= max * 0.9) return 'overstocked';
    return 'active';
  };

  const getStockPct = (item: InventoryItemBackend) => {
    const stock = Number(item.current_stock);
    const max = item.max_stock_level != null ? Number(item.max_stock_level) : null;
    if (!max || max === 0) return stock > 0 ? 50 : 0;
    return Math.min(100, Math.round((stock / max) * 100));
  };

  const categories = useMemo(() => [...new Set(inventory.map(i => i.category).filter(Boolean) as string[])], [inventory]);

  const filteredInventory = useMemo(() => {
    return inventory.filter(item => {
      const status = getItemStatus(item);
      const matchSearch = item.material_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (item.category ?? '').toLowerCase().includes(searchTerm.toLowerCase());
      const matchCat = filterCategory === 'all' || item.category === filterCategory;
      const matchWh = filterWarehouse === 'all' || item.warehouse_id === filterWarehouse;
      const matchStatus = filterStatus === 'all' || status === filterStatus;
      return matchSearch && matchCat && matchWh && matchStatus;
    });
  }, [inventory, searchTerm, filterCategory, filterWarehouse, filterStatus]);

  const stats = useMemo(() => {
    const active = inventory.filter(i => i.is_active);
    const totalValue = active.reduce((sum, i) =>
      sum + (i.unit_cost != null ? Number(i.current_stock) * Number(i.unit_cost) : 0), 0);
    const lowStock = active.filter(i => {
      const s = getItemStatus(i);
      return s === 'low' || s === 'critical';
    }).length;
    const outOfStock = active.filter(i => Number(i.current_stock) === 0).length;
    return { total: active.length, totalValue, lowStock, outOfStock };
  }, [inventory]);

  const getStatusBadge = (item: InventoryItemBackend) => {
    const s = getItemStatus(item);
    switch (s) {
      case 'active': return <Badge className="bg-green-100 text-green-800 border-green-200">Active</Badge>;
      case 'low': return <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200"><AlertTriangle className="h-3 w-3 mr-1" />Low Stock</Badge>;
      case 'critical': return <Badge className="bg-orange-100 text-orange-800 border-orange-200"><AlertTriangle className="h-3 w-3 mr-1" />Critical</Badge>;
      case 'out_of_stock': return <Badge className="bg-red-100 text-red-800 border-red-200">Out of Stock</Badge>;
      case 'overstocked': return <Badge className="bg-blue-100 text-blue-800 border-blue-200">Overstocked</Badge>;
      case 'inactive': return <Badge variant="secondary">Inactive</Badge>;
      default: return <Badge variant="secondary">{s}</Badge>;
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h2 className="text-2xl font-semibold">Inventory Management</h2>
          <p className="text-muted-foreground">Track and manage all materials and stock levels</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setWarehouseManageOpen(true)} className="gap-2">
            <Settings className="h-4 w-4" />
            Warehouses
          </Button>
          <Button onClick={() => setAddOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Add Material
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Package className="h-4 w-4 text-blue-600" />Total Items
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-700">{stats.total}</div>
            <p className="text-xs text-blue-600 mt-1">Active in inventory</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Warehouse className="h-4 w-4 text-green-600" />Total Value
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-700">{formatCurrencyCompact(stats.totalValue, orgCurrency)}</div>
            <p className="text-xs text-green-600 mt-1">Current inventory value</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-yellow-50 to-yellow-100 border-yellow-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-yellow-600" />Low Stock
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-700">{stats.lowStock}</div>
            <p className="text-xs text-yellow-600 mt-1">Below threshold</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <TrendingDown className="h-4 w-4 text-red-600" />Out of Stock
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-700">{stats.outOfStock}</div>
            <p className="text-xs text-red-600 mt-1">Needs reorder</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by name or category..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="flex gap-2 flex-wrap">
              <Select value={filterCategory} onValueChange={setFilterCategory}>
                <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={filterWarehouse} onValueChange={setFilterWarehouse}>
                <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Warehouses</SelectItem>
                  {warehouses.map(w => <SelectItem key={w.warehouse_id} value={w.warehouse_id}>{w.warehouse_name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="low">Low Stock</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                  <SelectItem value="out_of_stock">Out of Stock</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" size="icon"><Filter className="h-4 w-4" /></Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardHeader>
          <CardTitle>Material Inventory</CardTitle>
          <CardDescription>Showing {filteredInventory.length} of {inventory.length} items</CardDescription>
        </CardHeader>
        <CardContent>
          {filteredInventory.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Package className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p className="font-medium">No inventory items found</p>
              <p className="text-sm mt-1">Add materials using the button above</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Material</TableHead>
                    <TableHead>Stock Level</TableHead>
                    <TableHead>Value & Cost</TableHead>
                    <TableHead>Warehouse</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredInventory.map(item => {
                    const pct = getStockPct(item);
                    const stock = Number(item.current_stock);
                    const totalVal = item.unit_cost != null ? stock * Number(item.unit_cost) : null;
                    return (
                      <TableRow key={item.inventory_item_id} className="hover:bg-muted/50">
                        <TableCell>
                          <div className="space-y-1">
                            <p className="font-medium">{item.material_name}</p>
                            {item.category && <p className="text-sm text-muted-foreground">{item.category}</p>}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-2 min-w-[140px]">
                            <div className="flex justify-between text-sm">
                              <span>{stock} {item.unit_of_measure}</span>
                              {item.max_stock_level && <span>{pct}%</span>}
                            </div>
                            {item.max_stock_level && <Progress value={pct} className="h-2" />}
                            <p className="text-xs text-muted-foreground">
                              Min: {item.min_stock_level != null ? Number(item.min_stock_level) : '—'} | Max: {item.max_stock_level != null ? Number(item.max_stock_level) : '—'}
                            </p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            <p className="font-medium">{totalVal != null ? formatCurrencyCompact(totalVal, orgCurrency) : '—'}</p>
                            {item.unit_cost != null && <p className="text-muted-foreground">{formatCurrencyCompact(Number(item.unit_cost), orgCurrency)}/{item.unit_of_measure}</p>}
                          </div>
                        </TableCell>
                        <TableCell className="text-sm">{warehouseMap[item.warehouse_id ?? ''] ?? '—'}</TableCell>
                        <TableCell>{getStatusBadge(item)}</TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon"><MoreVertical className="h-4 w-4" /></Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => setViewItem(item)}>
                                <Eye className="h-4 w-4 mr-2" />View Details
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => setEditItem(item)}>
                                <Edit className="h-4 w-4 mr-2" />Edit Material
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => setAdjustItem(item)}>
                                <TrendingUp className="h-4 w-4 mr-2" />Adjust Stock
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => setDeactivateItem(item)} className="text-destructive">
                                <Ban className="h-4 w-4 mr-2" />Deactivate
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modals — rendered outside table to avoid Radix hierarchy issues */}
      {addOpen && (
        <MaterialModal
          open={addOpen}
          onOpenChange={setAddOpen}
          mode="add"
          orgId={orgId!}
          orgCurrency={orgCurrency}
          warehouses={warehouses}
          onSuccess={refetch}
        />
      )}
      {editItem && (
        <MaterialModal
          open={!!editItem}
          onOpenChange={(v) => { if (!v) setEditItem(null); }}
          mode="edit"
          item={editItem}
          orgId={orgId!}
          orgCurrency={orgCurrency}
          warehouses={warehouses}
          onSuccess={refetch}
        />
      )}
      {viewItem && (
        <ViewDetailsModal
          open={!!viewItem}
          onOpenChange={(v) => { if (!v) setViewItem(null); }}
          item={viewItem}
          warehouseName={warehouseMap[viewItem.warehouse_id ?? '']}
          orgCurrency={orgCurrency}
        />
      )}
      {adjustItem && (
        <AdjustStockModal
          open={!!adjustItem}
          onOpenChange={(v) => { if (!v) setAdjustItem(null); }}
          item={adjustItem}
          onSuccess={refetch}
        />
      )}
      {deactivateItem && (
        <DeactivateDialog
          open={!!deactivateItem}
          onOpenChange={(v) => { if (!v) setDeactivateItem(null); }}
          item={deactivateItem}
          onSuccess={refetch}
        />
      )}
      <WarehouseManagementDialog
        open={warehouseManageOpen}
        onOpenChange={setWarehouseManageOpen}
        orgId={orgId!}
        onWarehousesChanged={() => queryClient.invalidateQueries({ queryKey: queryKeys.warehouses.all(orgId ?? '') })}
      />
    </div>
  );
}
